/**
 * Resolves SLC tier for PCO/EOL shared-root milestone default-approver lookup.
 * Mirrors backend {@code PhasedMomentMilestoneContextResolver.resolveHighestPrioritySlcTier}.
 */

import { isEolMoment } from 'utils/constants';
import { MOMENT_TYPE_PRICE_CHANGES_AND_OFFERS } from '../configs/momentTypeConstants';

const EOL_INITIAL_ANNOUNCEMENT = 'Initial Announcement';

export type SharedRootTierPhase = {
  id?: string;
  phaseName?: string;
  status?: string;
  generalInfo?: { tier?: string };
};

export type SharedRootTierMoment = {
  momentType?: string;
  generalInfo?: { tier?: string };
  releasePhases?: SharedRootTierPhase[];
};

function slcTierRank(tier: string): number {
  const t = tier.trim().toUpperCase();
  if (t === 'L1') return 1;
  if (t === 'L2') return 2;
  if (t === 'L3') return 3;
  if (t === 'OTHER') return 4;
  if (t === 'TBD') return 5;
  return 100;
}

function isPhaseNaStatus(status?: string | null): boolean {
  if (!status?.trim()) return false;
  const n = status.trim().toUpperCase();
  return n === 'N/A' || n === 'NA';
}

function isActiveEligibleStatus(status?: string | null): boolean {
  if (!status?.trim()) return false;
  if (isPhaseNaStatus(status)) return false;
  return status.trim().toUpperCase() === 'ACTIVE';
}

function isEligiblePhase(
  phase: SharedRootTierPhase | null | undefined
): boolean {
  return phase != null && isActiveEligibleStatus(phase.status);
}

function highestTierAmongActivePhases(
  phases: SharedRootTierPhase[],
  excludedPhaseName?: string | null
): string | null {
  let bestTier: string | null = null;
  let bestRank = Number.MAX_SAFE_INTEGER;

  for (const phase of phases) {
    if (!phase) continue;
    if (
      excludedPhaseName &&
      phase.phaseName?.toLowerCase() === excludedPhaseName.toLowerCase()
    ) {
      continue;
    }
    if (!isEligiblePhase(phase)) continue;

    const tier = phase.generalInfo?.tier?.trim();
    if (!tier) continue;

    const rank = slcTierRank(tier);
    if (rank < bestRank) {
      bestRank = rank;
      bestTier = tier;
    }
  }
  return bestTier;
}

function findPhaseByName(
  phases: SharedRootTierPhase[],
  name: string
): SharedRootTierPhase | undefined {
  return phases.find(p => p.phaseName?.toLowerCase() === name.toLowerCase());
}

/** EOL: IA tier when ACTIVE; otherwise highest-priority tier among other ACTIVE phases. */
function resolveEolSlcTier(phases: SharedRootTierPhase[]): string | null {
  const ia = findPhaseByName(phases, EOL_INITIAL_ANNOUNCEMENT);
  if (ia && isEligiblePhase(ia)) {
    const iaTier = ia.generalInfo?.tier?.trim();
    if (iaTier) return iaTier;
  }
  return highestTierAmongActivePhases(
    phases,
    ia?.phaseName ?? EOL_INITIAL_ANNOUNCEMENT
  );
}

/** PCO: highest-priority ACTIVE phase tier, then root generalInfo.tier. */
function resolvePcoSlcTier(
  moment: SharedRootTierMoment,
  phases: SharedRootTierPhase[]
): string | null {
  const fromPhases = highestTierAmongActivePhases(phases);
  if (fromPhases) return fromPhases;
  const rootTier = moment.generalInfo?.tier?.trim();
  return rootTier || null;
}

/**
 * Tier used for default-approver API on shared-root milestone grids (PCO/EOL).
 * Returns null when moment is not PCO/EOL or no tier can be resolved from phases.
 */
export function resolveSharedRootMilestoneTier(
  moment: SharedRootTierMoment | null | undefined
): string | null {
  if (!moment?.releasePhases?.length) return null;
  const phases = moment.releasePhases;
  const momentType = moment.momentType ?? '';

  if (isEolMoment(momentType)) {
    return resolveEolSlcTier(phases);
  }
  if (momentType === MOMENT_TYPE_PRICE_CHANGES_AND_OFFERS) {
    return resolvePcoSlcTier(moment, phases);
  }
  return null;
}

/** Same as {@link resolveSharedRootMilestoneTier} with L1 fallback for legacy callers. */
export function resolveSharedRootMilestoneTierOrDefault(
  moment: SharedRootTierMoment | null | undefined
): string {
  return resolveSharedRootMilestoneTier(moment) ?? 'L1';
}
